import { OrganizationLayout } from '../../layouts/organization-layout'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Separator } from '../../components/ui/separator'
import { Link, usePage } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/i18n'
import { isAdmin as checkIsAdmin } from '@/lib/auth'

interface Organization {
  id: number
  name: string
  description: string | null
  created_at: string
  updated_at: string
}

interface OrganizationShowProps {
  organization: Organization
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      organization_role: string
    }
  }
  flash?: {
    success?: string
    error?: string
  }
  [key: string]: unknown
}

function Show({ organization }: OrganizationShowProps) {
  const { props } = usePage<OrganizationShowProps>()
  const auth = props.auth
  const isAdmin = checkIsAdmin(auth.user)
  const { t } = useTranslation('organizations')

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <div className="mx-auto max-w-4xl space-y-8">

            {/* General Information */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t('show.generalInfo.title')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('show.generalInfo.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border p-4">
                <p className="text-xs font-medium text-muted-foreground">{t('show.fields.name')}</p>
                <p className="mt-1 text-lg font-semibold">{organization.name}</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-medium text-muted-foreground">{t('show.fields.status')}</p>
                <div className="mt-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">
                    {t('show.status.active')}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <p className="text-xs font-medium text-muted-foreground">{t('show.fields.description')}</p>
              <p className="mt-1 text-sm">
                {organization.description || (
                  <span className="text-muted-foreground italic">{t('show.fields.noDescription')}</span>
                )}
              </p>
            </div>

            <Separator />

            {/* Account Details */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t('show.accountDetails.title')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t('show.accountDetails.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border p-4">
                <p className="text-xs font-medium text-muted-foreground">{t('show.fields.created')}</p>
                <p className="mt-1 text-sm font-semibold">
                  {new Date(organization.created_at).toLocaleDateString(getDateLocale(), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-xs font-medium text-muted-foreground">{t('show.fields.updated')}</p>
                <p className="mt-1 text-sm font-semibold">
                  {new Date(organization.updated_at).toLocaleDateString(getDateLocale(), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>

            {isAdmin && (
              <div className="flex justify-end">
                <Button asChild className="max-sm:w-full">
                  <Link href="/organization/edit">{t('show.editButton')}</Link>
                </Button>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  )
}

Show.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default Show
